import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Banner, Link, Text } from '@betterment/samba-react';

import { CsvExportStatus } from '../../../../graphql/schema';
import { DismissFirmBillableAccountsCsvExportDocument } from '../graphql/mutation.dismissFirmBillableAccountsCsvExport.generated';

type BannerVariants = 'success' | 'warning' | 'info';

const bannerVariants: Record<CsvExportStatus, { variant: BannerVariants; copy: string }> = {
  [CsvExportStatus.Generating]: { variant: 'info', copy: 'Generating CSV template. This may take a few minutes.' },
  [CsvExportStatus.Complete]: { variant: 'success', copy: 'Your CSV template is ready.' },
  [CsvExportStatus.Error]: { variant: 'warning', copy: 'CSV template generation failed. Please try again later.' },
};

export const FirmBillableAccountsCsvExportBanner = ({
  firmBillableAccountsCsvExportStatus,
  onDismiss,
}: {
  firmBillableAccountsCsvExportStatus: CsvExportStatus;
  onDismiss: () => void;
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [dismissBanner] = useGraphQLMutation(DismissFirmBillableAccountsCsvExportDocument);

  const isGenerating = firmBillableAccountsCsvExportStatus === CsvExportStatus.Generating;
  const isCompleted = firmBillableAccountsCsvExportStatus === CsvExportStatus.Complete;
  const { variant, copy } = bannerVariants[firmBillableAccountsCsvExportStatus];

  const handleDismiss = async () => {
    await dismissBanner();
    setIsDismissed(true);
    onDismiss();
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Banner variant={variant} data-testid="csv-template-download-banner">
      <Banner.Content>
        <Text as="p">
          {copy}{' '}
          {isCompleted && (
            <Link href="/firm_billable_accounts_csv_export" download="billable_accounts.csv">
              Download
            </Link>
          )}
        </Text>
      </Banner.Content>
      {!isGenerating && (
        <Banner.Close
          onClick={() => {
            void handleDismiss();
          }}
          data-testid="csv-template-download-banner-close-button"
        />
      )}
    </Banner>
  );
};
